import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Ruler, Camera, ChevronLeft, ChevronRight } from 'lucide-react';
import PhotoCapture from './PhotoCapture';
import PhotoGalleryModal from './PhotoGalleryModal';

export interface DisplayablePhotoData {
  displayId: string;
  url: string;
  dataUrl?: string;
  fileName: string;
  uploading?: boolean;
  error?: string;
  note?: string;
}

interface RoomHeightPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (height: number, fullHeightImages: DisplayablePhotoData[]) => void;
  roomName?: string;
  defaultHeight?: number;
  isFullscreen?: boolean;
}

const RoomHeightPrompt: React.FC<RoomHeightPromptProps> = ({
  isOpen,
  onClose,
  onSave,
  roomName = 'New Room',
  defaultHeight = 2.4,
  isFullscreen = false
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [height, setHeight] = useState<string>(defaultHeight.toString());
  const [error, setError] = useState<string>('');
  const [fullHeightImages, setFullHeightImages] = useState<DisplayablePhotoData[]>([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setHeight(defaultHeight.toString());
      setError('');
      setFullHeightImages([]);
    }
  }, [isOpen, defaultHeight]);

  const handleNextStep = () => {
    const heightValue = parseFloat(height);
    
    if (isNaN(heightValue) || heightValue <= 0) {
      setError('Please enter a valid height greater than 0');
      return;
    }
    
    if (heightValue > 10) {
      setError('Height seems too high. Please enter a reasonable value (max 10m)');
      return;
    }

    setError('');
    setStep(2);
  };

  const handleSave = () => {
    const heightValue = parseFloat(height);
    onSave(heightValue, fullHeightImages);
    onClose();
  };

  const handleSkip = () => {
    const heightValue = parseFloat(height);
    onSave(heightValue, []);
    onClose();
  };

  const handlePhotosCaptured = (photos: Array<{ base64String: string; format: string }>) => {
    const newPhotos: DisplayablePhotoData[] = photos.map((photo, index) => ({
      displayId: `fullheight-${Date.now()}-${index}`,
      url: `data:image/${photo.format};base64,${photo.base64String}`,
      dataUrl: `data:image/${photo.format};base64,${photo.base64String}`,
      fileName: `fullheight_${Date.now()}_${index}.${photo.format}`,
      uploading: false
    }));
    setFullHeightImages(prev => [...prev, ...newPhotos]);
  };

  const handleDeletePhoto = (photoId: string) => {
    setFullHeightImages(prev => prev.filter(p => p.displayId !== photoId));
  };

  const handleUpdateNote = (photoId: string, note: string) => {
    setFullHeightImages(prev => prev.map(p => 
      p.displayId === photoId ? { ...p, note } : p
    ));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && step === 1) {
      handleNextStep();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className={`fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 ${isFullscreen ? 'z-[10003]' : 'z-[999998]'}`}>
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-md border border-white/20">
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 rounded-full p-2">
                  {step === 1 ? <Ruler className="w-5 h-5 text-white" /> : <Camera className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">
                    {step === 1 ? 'Set Room Height' : 'Full Height Door Images'}
                  </h3>
                  <p className="text-indigo-100 text-sm">
                    {step === 1 ? 'Configure the room ceiling height' : 'Capture door height reference'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
              >
                <X size={20} />
              </button>
            </div>
            {/* Step Indicator */}
            <div className="flex items-center space-x-2 mt-4 relative z-10">
              <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                step >= 1 ? 'bg-white' : 'bg-white/30'
              }`}></div>
              <div className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                step >= 2 ? 'bg-white' : 'bg-white/30'
              }`}></div>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-400/20 to-pink-400/20 backdrop-blur-3xl"></div>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6 bg-gradient-to-b from-gray-50/50 to-white">
          <div className="text-center">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200/50">
              <p className="text-sm text-gray-700 font-medium">
                {step === 1 ? 'Setting height for:' : 'Capture images for:'} <span className="text-indigo-600 font-semibold">"{roomName}"</span>
              </p>
            </div>
          </div>
          
          {/* Step 1: Height Entry */}
          {step === 1 && (
            <>
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-900 mb-3">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                  Height (meters)
                </label>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => {
                    setHeight(e.target.value);
                    setError('');
                  }}
                  onKeyDown={handleKeyPress}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-900 placeholder-gray-500 text-center text-lg font-medium"
                  placeholder="e.g., 2.4"
                  step="0.1"
                  min="0.1"
                  max="10"
                />
                {error && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-600 text-sm font-medium">{error}</p>
                  </div>
                )}
              </div>
              
              {/* Quick Height Presets */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                  Common Heights
                </h4>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => {
                      setHeight('2.2');
                      setError('');
                    }}
                    className="p-3 bg-gradient-to-r from-gray-50 to-indigo-50/30 rounded-xl border border-gray-200/50 hover:border-indigo-300 hover:from-indigo-50 hover:to-indigo-100/50 transition-all duration-200 text-center"
                  >
                    <div className="text-sm font-medium text-gray-900">2.2m</div>
                    <div className="text-xs text-gray-500">Basement</div>
                  </button>
                  <button
                    onClick={() => {
                      setHeight('2.4');
                      setError('');
                    }}
                    className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50/30 rounded-xl border border-indigo-200/50 hover:border-indigo-400 hover:from-indigo-100 hover:to-purple-100/50 transition-all duration-200 text-center ring-2 ring-indigo-200/30"
                  >
                    <div className="text-sm font-medium text-indigo-700">2.4m</div>
                    <div className="text-xs text-indigo-600">Standard</div>
                  </button>
                  <button
                    onClick={() => {
                      setHeight('3.0');
                      setError('');
                    }}
                    className="p-3 bg-gradient-to-r from-gray-50 to-purple-50/30 rounded-xl border border-gray-200/50 hover:border-purple-300 hover:from-purple-50 hover:to-purple-100/50 transition-all duration-200 text-center"
                  >
                    <div className="text-sm font-medium text-gray-900">3.0m</div>
                    <div className="text-xs text-gray-500">High</div>
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Step 2: Photo Capture */}
          {step === 2 && (
            <>
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                  Capture Full Height Door Images
                </h4>
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <p className="text-sm text-blue-800">
                    📸 Take photos of doors from floor to ceiling to document the full height of internal doors. This helps verify room height measurements.
                  </p>
                </div>
              </div>

              {/* Photo Capture Component */}
              <PhotoCapture
                onPhotosCaptured={handlePhotosCaptured}
                buttonLabel="Take Photos"
                maxPhotos={10}
              />

              {/* Captured Images Preview */}
              {fullHeightImages.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Captured Images ({fullHeightImages.length})
                  </h4>
                  <button
                    onClick={() => setIsGalleryOpen(true)}
                    className="relative w-full aspect-video rounded-xl overflow-hidden border-2 border-indigo-200 hover:border-indigo-400 transition-all group"
                  >
                    <img
                      src={fullHeightImages[0].dataUrl || fullHeightImages[0].url}
                      alt="Full height door"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="text-2xl font-bold">{fullHeightImages.length}</div>
                        <div className="text-sm">Click to view all</div>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white rounded-b-2xl">
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                Cancel
              </button>
              <button
                onClick={handleNextStep}
                disabled={!height || parseFloat(height) <= 0 || parseFloat(height) > 10}
                className={`px-6 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg ${
                  height && parseFloat(height) > 0 && parseFloat(height) <= 10
                    ? 'text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:shadow-xl hover:scale-105 ring-2 ring-indigo-200/50'
                    : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                }`}
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep(1)}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md flex items-center space-x-2"
              >
                <ChevronLeft size={16} />
                <span>Back</span>
              </button>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSkip}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Skip
                </button>
                <button
                  onClick={handleSave}
                  className={`px-6 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg ${
                    fullHeightImages.length > 0
                      ? 'text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 hover:shadow-xl hover:scale-105 ring-2 ring-green-200/50'
                      : 'text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:shadow-xl hover:scale-105 ring-2 ring-indigo-200/50'
                  }`}
                >
                  <Ruler size={16} />
                  <span>Save {fullHeightImages.length > 0 && `(${fullHeightImages.length})`}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Photo Gallery Modal */}
      {isGalleryOpen && (
        <PhotoGalleryModal
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          photos={fullHeightImages.map(img => ({
            id: img.displayId,
            url: img.url,
            localPath: img.dataUrl,
            fileName: img.fileName,
            uploading: img.uploading,
            error: img.error,
            note: (img as any).note
          }))}
          sectionTitle="Full Height Door Images"
          onDeletePhoto={handleDeletePhoto}
          onUpdateNote={handleUpdateNote}
          zIndex={10000001}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default RoomHeightPrompt; 